import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  MessageCircle, X, Send, ChevronLeft, Plus, 
  Megaphone, Check, Clock, User, Shield, Loader2
} from 'lucide-react';

export default function SupportChatWidget() {
  const { user, token } = useAuth();
  const { currentColors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list'); // list, chat, new, broadcasts
  const [conversations, setConversations] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const inputRef = useRef(null);
  const chatPanelRef = useRef(null);

  // Close chat when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && chatPanelRef.current && !chatPanelRef.current.contains(event.target)) {
        // Check if click is on the toggle button
        const toggleBtn = document.querySelector('[data-testid="support-chat-toggle"]');
        if (toggleBtn && toggleBtn.contains(event.target)) {
          return; // Let the toggle button handle it
        }
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/support/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  }, [token]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/support/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  }, [token]);

  // Load broadcasts
  const loadBroadcasts = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/broadcasts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcasts(response.data.broadcasts || []);
    } catch (error) {
      console.error('Failed to load broadcasts:', error);
    }
  }, [token]);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId) => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/support/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setCurrentConversation(response.data.conversation);
      loadUnreadCount();
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  }, [token, loadUnreadCount]);

  // Initial load and polling
  useEffect(() => {
    if (user && token) {
      loadUnreadCount();
      pollIntervalRef.current = setInterval(loadUnreadCount, 30000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [user, token, loadUnreadCount]);

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && view === 'list') {
      loadConversations();
      loadBroadcasts();
    }
  }, [isOpen, view, loadConversations, loadBroadcasts]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when entering chat
  useEffect(() => {
    if (view === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [view]);

  // Poll for new messages in active chat
  useEffect(() => {
    let interval;
    if (view === 'chat' && currentConversation) {
      interval = setInterval(() => {
        loadMessages(currentConversation.id);
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [view, currentConversation, loadMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startNewConversation = async () => {
    if (!newSubject.trim() || !newMessage.trim()) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/support/conversations`,
        { subject: newSubject, message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewSubject('');
      setNewMessage('');
      await loadConversations();
      openConversation(response.data.conversation);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentConversation || sending) return;
    
    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);
    
    // Optimistic update
    const tempMsg = {
      id: 'temp-' + Date.now(),
      message: messageText,
      sender_id: user.id,
      sender_name: user.name,
      sender_role: 'user',
      created_at: new Date().toISOString(),
      pending: true
    };
    setMessages(prev => [...prev, tempMsg]);
    scrollToBottom();
    
    try {
      await axios.post(
        `${API}/support/conversations/${currentConversation.id}/messages`,
        { message: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reload to get actual message with server ID
      await loadMessages(currentConversation.id);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      setNewMessage(messageText);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const openConversation = (conv) => {
    setCurrentConversation(conv);
    loadMessages(conv.id);
    setView('chat');
  };

  const goBack = () => {
    setView('list');
    setCurrentConversation(null);
    setMessages([]);
    loadConversations();
  };

  const markBroadcastRead = async (broadcastId) => {
    try {
      await axios.post(
        `${API}/broadcasts/${broadcastId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadBroadcasts();
      loadUnreadCount();
    } catch (error) {
      console.error('Failed to mark broadcast read:', error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (view === 'chat') {
        sendMessage();
      } else if (view === 'new' && newSubject.trim() && newMessage.trim()) {
        startNewConversation();
      }
    }
  };

  if (!user) return null;

  const unreadBroadcasts = broadcasts.filter(b => !b.is_read).length;
  const totalUnread = unreadCount + unreadBroadcasts;

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' };
      case 'in_progress': return { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' };
      default: return { bg: 'rgba(251, 191, 36, 0.15)', text: '#fbbf24' };
    }
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-[9999] transition-all duration-300 hover:scale-110 active:scale-95"
        style={{ 
          background: `linear-gradient(135deg, ${currentColors.primary}, ${currentColors.secondary})`,
          boxShadow: `0 4px 20px ${currentColors.primary}40`
        }}
        data-testid="support-chat-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-white" />
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-medium">
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          ref={chatPanelRef}
          className="fixed bottom-24 right-6 w-[380px] max-w-[calc(100vw-48px)] rounded-2xl shadow-2xl flex flex-col overflow-hidden z-[9998]"
          style={{ 
            height: '520px',
            maxHeight: 'calc(100vh - 140px)',
            backgroundColor: currentColors.cardBg,
            border: `1px solid ${currentColors.border}`,
            boxShadow: `0 10px 40px rgba(0,0,0,0.2)`
          }}
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div 
            className="flex-shrink-0 px-4 py-3 flex items-center gap-3"
            style={{ 
              background: `linear-gradient(135deg, ${currentColors.primary}15, ${currentColors.secondary}15)`,
              borderBottom: `1px solid ${currentColors.border}`
            }}
          >
            {view !== 'list' && (
              <button 
                onClick={goBack}
                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                data-testid="chat-back-btn"
              >
                <ChevronLeft className="w-5 h-5" style={{ color: currentColors.text }} />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate" style={{ color: currentColors.text }}>
                {view === 'list' && 'Support'}
                {view === 'chat' && (currentConversation?.subject || 'Conversation')}
                {view === 'new' && 'New Conversation'}
                {view === 'broadcasts' && 'Announcements'}
              </h3>
              {view === 'list' && (
                <p className="text-xs" style={{ color: currentColors.textMuted }}>
                  We're here to help
                </p>
              )}
              {view === 'chat' && currentConversation && (
                <span 
                  className="inline-block text-[10px] px-2 py-0.5 rounded-full mt-0.5"
                  style={getStatusColor(currentConversation.status)}
                >
                  {currentConversation.status?.replace('_', ' ') || 'open'}
                </span>
              )}
            </div>
            {view === 'list' && (
              <div className="flex gap-1">
                <button
                  onClick={() => setView('broadcasts')}
                  className="p-2 rounded-lg transition-colors hover:bg-white/10 relative"
                  title="Announcements"
                  data-testid="broadcasts-btn"
                >
                  <Megaphone className="w-5 h-5" style={{ color: currentColors.primary }} />
                  {unreadBroadcasts > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                      {unreadBroadcasts}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setView('new')}
                  className="p-2 rounded-lg transition-colors hover:bg-white/10"
                  title="New conversation"
                  data-testid="new-conversation-btn"
                >
                  <Plus className="w-5 h-5" style={{ color: currentColors.primary }} />
                </button>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto">
            {/* Conversations List View */}
            {view === 'list' && (
              <div className="p-3 space-y-2">
                {conversations.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: currentColors.textMuted }} />
                    <p className="text-sm mb-3" style={{ color: currentColors.textMuted }}>No conversations yet</p>
                    <button
                      onClick={() => setView('new')}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{ backgroundColor: `${currentColors.primary}15`, color: currentColors.primary }}
                      data-testid="start-conversation-btn"
                    >
                      Start a conversation
                    </button>
                  </div>
                ) : (
                  conversations.map(conv => (
                    <button
                      key={conv.id}
                      onClick={() => openConversation(conv)}
                      className="w-full p-3 rounded-xl text-left transition-all hover:translate-x-1"
                      style={{ 
                        backgroundColor: conv.unread_count > 0 ? `${currentColors.primary}10` : `${currentColors.background}40`,
                        border: `1px solid ${conv.unread_count > 0 ? currentColors.primary + '30' : currentColors.border}`
                      }}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate" style={{ color: currentColors.text }}>
                            {conv.subject}
                          </p>
                          <p className="text-xs truncate mt-1 opacity-70" style={{ color: currentColors.textMuted }}>
                            {conv.last_message}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-[10px]" style={{ color: currentColors.textMuted }}>
                            {formatTime(conv.last_message_at)}
                          </span>
                          {conv.unread_count > 0 && (
                            <span 
                              className="w-5 h-5 rounded-full text-[10px] text-white flex items-center justify-center"
                              style={{ backgroundColor: currentColors.primary }}
                            >
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span 
                          className="text-[10px] px-2 py-0.5 rounded-full"
                          style={getStatusColor(conv.status)}
                        >
                          {conv.status?.replace('_', ' ') || 'open'}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Chat View */}
            {view === 'chat' && (
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {loading && messages.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin" style={{ color: currentColors.primary }} />
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isUser = msg.sender_role !== 'admin';
                      const showAvatar = index === 0 || messages[index - 1]?.sender_role !== msg.sender_role;
                      
                      return (
                        <div 
                          key={msg.id}
                          className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
                        >
                          {/* Avatar */}
                          <div className={`flex-shrink-0 w-8 ${showAvatar ? 'visible' : 'invisible'}`}>
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ 
                                backgroundColor: isUser ? `${currentColors.primary}20` : `${currentColors.secondary}20`
                              }}
                            >
                              {isUser ? (
                                <User className="w-4 h-4" style={{ color: currentColors.primary }} />
                              ) : (
                                <Shield className="w-4 h-4" style={{ color: currentColors.secondary }} />
                              )}
                            </div>
                          </div>
                          
                          {/* Message Bubble */}
                          <div 
                            className={`max-w-[75%] p-3 rounded-2xl ${isUser ? 'rounded-br-md' : 'rounded-bl-md'} ${msg.pending ? 'opacity-70' : ''}`}
                            style={{ 
                              backgroundColor: isUser ? `${currentColors.primary}15` : `${currentColors.secondary}10`,
                              border: `1px solid ${isUser ? currentColors.primary : currentColors.secondary}20`
                            }}
                          >
                            {showAvatar && (
                              <p className="text-[10px] font-medium mb-1" style={{ color: currentColors.textMuted }}>
                                {msg.sender_name} {!isUser && '(Support)'}
                              </p>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words" style={{ color: currentColors.text }}>
                              {msg.message}
                            </p>
                            <p className="text-[10px] mt-1.5 opacity-60" style={{ color: currentColors.textMuted }}>
                              {msg.pending ? 'Sending...' : formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}

            {/* New Conversation View */}
            {view === 'new' && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: currentColors.textMuted }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    placeholder="What do you need help with?"
                    className="w-full px-3 py-2.5 rounded-xl text-sm transition-colors"
                    style={{ 
                      backgroundColor: `${currentColors.background}60`,
                      border: `1px solid ${currentColors.border}`,
                      color: currentColors.text,
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                    onBlur={(e) => e.target.style.borderColor = currentColors.border}
                    data-testid="new-subject-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: currentColors.textMuted }}>
                    Message
                  </label>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Describe your issue in detail..."
                    rows={5}
                    className="w-full px-3 py-2.5 rounded-xl text-sm resize-none transition-colors"
                    style={{ 
                      backgroundColor: `${currentColors.background}60`,
                      border: `1px solid ${currentColors.border}`,
                      color: currentColors.text,
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                    onBlur={(e) => e.target.style.borderColor = currentColors.border}
                    data-testid="new-message-input"
                  />
                </div>
                <button
                  onClick={startNewConversation}
                  disabled={!newSubject.trim() || !newMessage.trim() || sending}
                  className="w-full py-2.5 rounded-xl font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: currentColors.primary,
                    color: 'white'
                  }}
                  data-testid="submit-conversation-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Start Conversation
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Broadcasts View */}
            {view === 'broadcasts' && (
              <div className="p-3 space-y-2">
                {broadcasts.length === 0 ? (
                  <div className="text-center py-12">
                    <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: currentColors.textMuted }} />
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>No announcements yet</p>
                  </div>
                ) : (
                  broadcasts.map(broadcast => (
                    <div
                      key={broadcast.id}
                      className="p-3 rounded-xl transition-all"
                      style={{ 
                        backgroundColor: broadcast.is_read ? `${currentColors.background}40` : `${currentColors.primary}10`,
                        border: `1px solid ${broadcast.is_read ? currentColors.border : currentColors.primary + '30'}`
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm" style={{ color: currentColors.text }}>
                            {broadcast.title}
                          </h4>
                          <p className="text-xs mt-1.5 opacity-80" style={{ color: currentColors.textMuted }}>
                            {broadcast.message}
                          </p>
                          <div className="flex items-center gap-1.5 mt-2">
                            <Clock className="w-3 h-3" style={{ color: currentColors.textMuted }} />
                            <span className="text-[10px]" style={{ color: currentColors.textMuted }}>
                              {formatTime(broadcast.created_at)}
                            </span>
                          </div>
                        </div>
                        {!broadcast.is_read && (
                          <button
                            onClick={() => markBroadcastRead(broadcast.id)}
                            className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" style={{ color: currentColors.primary }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Message Input - Only in Chat View */}
          {view === 'chat' && currentConversation && (
            <div 
              className="flex-shrink-0 p-3 border-t"
              style={{ borderColor: currentColors.border, backgroundColor: `${currentColors.cardBg}` }}
            >
              <div className="flex gap-2 items-end">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  disabled={sending}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm transition-colors disabled:opacity-50"
                  style={{ 
                    backgroundColor: `${currentColors.background}60`,
                    border: `1px solid ${currentColors.border}`,
                    color: currentColors.text,
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                  onBlur={(e) => e.target.style.borderColor = currentColors.border}
                  data-testid="chat-message-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="p-2.5 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: currentColors.primary }}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
